import type { CandidateHistory, CandidateHistoryQueryData } from './useCandidateHistoryQuery.types';

import { useMutation } from '@tanstack/react-query';
import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { queryClient } from 'config/queryClient';
import { useGTM } from 'hooks/gtm';
import { useContext } from 'react';
import { CandidateHistoryAction, CandidateStatus } from 'services/candidates/types';
import { extractLastRefresh } from 'shared/legacy/contexts/CandidateContext/searchv3/extractLastRefresh';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { QueryKey } from 'types/query';
import { useCurrentUserQuery } from './useCurrentUserQuery';

type CandidateNotesMutationProps = {
  es_person_id: string;
  campaignId: string | number;
  status: CandidateStatus;
};

type CandidateNotesMutationParams = {
  note?: string;
  noteId?: number;
  isDeleting?: boolean;
  candidate: CandidateNotesMutationProps;
};

export const useCandidateNotesMutation = () => {
  const gtm = useGTM();
  const { data: currentUser } = useCurrentUserQuery();
  const { language } = useContext(LocalizationContext);

  return useMutation(
    async ({ note, noteId, isDeleting, candidate }: CandidateNotesMutationParams) => {
      let data;

      if (!noteId) {
        const response = await campaignsClient.postCampaignCandidateNotes({
          es_person_id: candidate.es_person_id,
          campaign_id: candidate.campaignId,
          notes: note!,
        });
        data = response.data;
      } else if (note && !isDeleting) {
        const response = await campaignsClient.putCampaignCandidateNotes({
          note_id: noteId!,
          es_person_id: candidate.es_person_id,
          campaign_id: candidate.campaignId,
          notes: note!,
        });
        data = response.data;
      } else if (isDeleting) {
        await campaignsClient.deleteCampaignCandidateNotes({
          note_id: noteId!,
          es_person_id: candidate.es_person_id,
        });
        data = null;
      }

      return { data, isNew: !noteId };
    },
    {
      onMutate: ({ isDeleting, noteId, candidate }) => {
        if (!isDeleting && !noteId) {
          const queryKey = [
            QueryKey.useCandidateHistoryQuery,
            { candidateEsPersonId: candidate.es_person_id, status: candidate.status },
          ];

          const existingData = queryClient.getQueryData<CandidateHistoryQueryData>(queryKey);
          if (!existingData || existingData.data.length === 0) return;

          queryClient.setQueryData<CandidateHistoryQueryData>(queryKey, (data) => {
            if (!data) return data;

            const lastCampaign = data.data.at(-1)?.campaign;

            const newNote: CandidateHistory = {
              campaign: { id: lastCampaign?.id ?? -1, name: lastCampaign?.name || 'loading...' },
              metadata: {},
              user: { id: -1 },
              action: CandidateHistoryAction.Note,
              id: Date.now(),
              by: { id: -1, name: currentUser?.first_name || 'loading...' },
              createdAt: new Date(),
              messageReceived: false,
              timeAgo: extractLastRefresh(new Date(), language),
            };

            return {
              ...data,
              data: [...data.data, newNote],
            };
          });
        }
      },
      onSuccess: async ({ isNew }, { candidate }) => {
        if (isNew) {
          gtm.updatedNotesTabOnCandidateCard(candidate.campaignId, candidate.es_person_id);
        }

        await queryClient.invalidateQueries([
          QueryKey.useCandidateHistoryQuery,
          { candidateEsPersonId: candidate.es_person_id },
        ]);
        await queryClient.invalidateQueries([QueryKey.useCandidateNotesQuery, candidate.es_person_id], {
          exact: false,
        });
      },
    },
  );
};
